import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { useCookies } from "react-cookie";
import { Modal, ModalBody, Form, FormGroup } from "reactstrap";
import Swal from "sweetalert2";
import Content from "layout/content/Content";
import Head from "layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Col,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
  TooltipComponent,
  RSelect,
} from "components/Component";
import { serviceGetProfitCenters, servicePostProfitCenter, servicePutProfitCenter } from "services/profitCenters";
import { serviceGetUsersBySales } from "services/users";

const PROFIT_CENTER_ROLES = ["ADMIN", "SYSTEM ADMIN", "SUPER ADMIN"];
const TAKE = 10;
const emptyForm = { id: "", code: "", name: "", description: "", isActive: true, sales: [] };

const userOption = (u) => ({ value: u.id, label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email });

const errorMessage = (resp) => {
  const msg = resp?.data?.message;
  if (Array.isArray(msg)) return msg.map((m) => m.property || m).join(", ") + " tidak valid";
  return msg || "Terjadi kesalahan";
};

const ProfitCenter = () => {
  const [cookies] = useCookies(["user"]);
  const isAllowed = PROFIT_CENTER_ROLES.includes(cookies.role?.name);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [search, setSearch] = useState("");
  const [salesOptions, setSalesOptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const refreshData = async (page = 1) => {
    const resp = await serviceGetProfitCenters(cookies.SIDikey, page, TAKE, search);
    if (resp.status === 200) {
      setData(resp.data.data);
      setMeta(resp.data.meta);
    }
  };

  useEffect(() => {
    if (isAllowed) refreshData(1);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAllowed) return;
    serviceGetUsersBySales(cookies.SIDikey, cookies.departmentIds, "Access_Own_Dept").then((resp) => {
      if (resp.status === 200) setSalesOptions(resp.data.map(userOption));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAllowed) return <Redirect to={`${process.env.PUBLIC_URL}/`} />;

  const openForm = (item) => {
    setForm(
      item
        ? {
            id: item.id,
            code: item.code,
            name: item.name,
            description: item.description || "",
            isActive: item.isActive,
            sales: item.sales.map((s) => userOption(s.user)),
          }
        : emptyForm
    );
    setModal(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      isActive: form.isActive,
      userIds: form.sales.map((s) => s.value),
    };
    setSaving(true);
    const resp = form.id
      ? await servicePutProfitCenter(cookies.SIDikey, form.id, payload)
      : await servicePostProfitCenter(cookies.SIDikey, payload);
    setSaving(false);
    if (/^20/.test(resp.status)) {
      Swal.fire("Profit Center", form.id ? "Berhasil diubah" : "Berhasil ditambahkan", "success");
      setModal(false);
      refreshData(form.id ? meta.page : 1);
    } else {
      Swal.fire("Profit Center", errorMessage(resp), "error");
    }
  };

  return (
    <React.Fragment>
      <Head title="Profit Center"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Profit Center
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="primary" onClick={() => openForm(null)}>
                <Icon name="plus"></Icon>
                <span>Tambah Baru</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <DataTable className="card-stretch">
            <div className="card-inner position-relative card-tools-toggle">
              <div className="form-control-wrap w-25">
                <div className="form-icon form-icon-left">
                  <Icon name="search"></Icon>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Cari kode / nama"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <DataTableBody>
              <DataTableHead>
                <DataTableRow>
                  <span className="sub-text">No</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Kode</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Nama</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Deskripsi</span>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="sub-text">Sales</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text">Status</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="sub-text"></span>
                </DataTableRow>
              </DataTableHead>
              {data.map((item, idx) => (
                <DataTableItem key={item.id}>
                  <DataTableRow>
                    <span>{(meta.page - 1) * meta.take + idx + 1}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <span>{item.code}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <span>{item.name}</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span>{item.description || "-"}</span>
                  </DataTableRow>
                  <DataTableRow size="md">
                    <span>{item.sales.map((s) => userOption(s.user).label).join(", ") || "-"}</span>
                  </DataTableRow>
                  <DataTableRow>
                    <span className={`badge badge-dim ${item.isActive ? "badge-success" : "badge-danger"}`}>
                      {item.isActive ? "Aktif" : "Nonaktif"}
                    </span>
                  </DataTableRow>
                  <DataTableRow>
                    <ul className="nk-tb-actions">
                      <li className="nk-tb-action" onClick={() => openForm(item)}>
                        <TooltipComponent
                          tag="a"
                          containerClassName="btn btn-trigger btn-icon"
                          id={"edit" + item.id}
                          icon="edit-alt-fill"
                          direction="top"
                          text="Edit"
                        />
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              ))}
            </DataTableBody>
            <div className="card-inner">
              {data.length > 0 ? (
                <PaginationComponent
                  itemPerPage={meta.take}
                  totalItems={meta.itemCount}
                  paginate={refreshData}
                  currentPage={meta.page}
                />
              ) : (
                <div className="text-center">
                  <span className="text-silent">No data found</span>
                </div>
              )}
            </div>
          </DataTable>
        </Block>
      </Content>

      <Modal isOpen={modal} toggle={() => setModal(false)} className="modal-dialog-centered" size="lg">
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            <h5 className="title">{form.id ? "Edit" : "Tambah"} Profit Center</h5>
            <Form className="row gy-4 mt-2" onSubmit={onSubmit}>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Kode</label>
                  <input
                    className="form-control"
                    required
                    maxLength={50}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Nama</label>
                  <input
                    className="form-control"
                    required
                    maxLength={255}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <label className="form-label">Deskripsi</label>
                  <textarea
                    className="form-control"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <label className="form-label">Sales</label>
                  <RSelect
                    isMulti
                    options={salesOptions}
                    value={form.sales}
                    onChange={(e) => setForm({ ...form, sales: e || [] })}
                    placeholder="Pilih sales"
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="profit-center-active"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  <label className="custom-control-label" htmlFor="profit-center-active">
                    Aktif
                  </label>
                </div>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button outline color="primary" type="button" onClick={() => setModal(false)}>
                      Batal
                    </Button>
                  </li>
                  <li>
                    <Button color="primary" type="submit" disabled={saving}>
                      Simpan
                    </Button>
                  </li>
                </ul>
              </Col>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ProfitCenter;
